// src/components/Dashboard.jsx

import React, { useEffect, useState } from "react";
import {
  Layout, Menu, Button, Typography, Row, Col, Card, Tag, Space, message, Spin
} from "antd";
import {
  ApiOutlined, SettingOutlined, FileSearchOutlined, PoweroffOutlined,
  PlusOutlined, LogoutOutlined, KeyOutlined
} from "@ant-design/icons";
import { motion } from "framer-motion";
import Conexiones from "./Conexiones";
import Configuracion from "./Configuracion";
import Token from "./Token";
import LogSistema from "./LogSistema";

const { Header, Content, Sider } = Layout;
const { Title, Text } = Typography;

const MENU_ITEMS = [
  { key: "inicio", icon: <ApiOutlined />, label: "Inicio" },
  { key: "conexiones", icon: <PlusOutlined />, label: "Conexiones" },
  { key: "config", icon: <SettingOutlined />, label: "Configuración Global" },
  { key: "log", icon: <FileSearchOutlined />, label: "Log del Sistema" },
  { key: "token", icon: <KeyOutlined />, label: "Generar Token" },
];

const BackendStatus = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ip, setIp] = useState("0.0.0.0");
  const [port, setPort] = useState(5000);
  const [spin, setSpin] = useState(false);
  const [pid, setPid] = useState(null);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await fetch("/launcher/status");
      const data = await res.json();
      setStatus(data.status === "running" ? "activo" : "inactivo");
      setPid(data.status === "running" ? data.pid : null);
    } catch {
      setStatus("inactivo");
      setPid(null);
    } finally {
      setLoading(false);
    }
  };

  const iniciarServidor = async () => {
    setSpin(true);
    try {
      const res = await fetch("/launcher/start-backend", { method: "POST" });
      const data = await res.json();
      if (data.status === "started" || data.status === "already_running") {
        message.success("Servidor iniciado");
        setStatus("activo");
        setPid(data.pid);
      } else {
        message.error("No se pudo iniciar el servidor");
      }
    } catch {
      message.error("Error al iniciar backend");
    } finally {
      setSpin(false);
    }
  };

  const detenerServidor = async () => {
    setSpin(true);
    try {
      const res = await fetch("/launcher/stop-backend", { method: "POST" });
      const data = await res.json();
      if (data.status === "stopped") {
        message.success("Servidor detenido");
        setStatus("inactivo");
        setPid(null);
      } else {
        message.warning("El servidor no estaba activo");
      }
    } catch {
      message.error("Error al detener backend");
    } finally {
      setSpin(false);
    }
  };

  const reiniciarServidor = async () => {
    setSpin(true);
    try {
      await fetch("/launcher/stop-backend", { method: "POST" });
      await new Promise((res) => setTimeout(res, 2000));
      const res2 = await fetch("/launcher/start-backend", { method: "POST" });
      const data = await res2.json();
      if (data.status === "started" || data.status === "already_running") {
        message.success("Servidor reiniciado");
        setStatus("activo");
        setPid(data.pid);
      } else {
        message.error("No se pudo reiniciar el servidor");
      }
    } catch {
      message.error("Error al reiniciar backend");
    } finally {
      setSpin(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4 }}
    >
      <Card className="futuristic-card" style={{ marginBottom: 24 }}>
        {loading ? (
          <Spin tip="Cargando estatus..." />
        ) : (
          <Row gutter={[16, 16]} align="middle">
            <Col span={24}>
              <Title level={4}>Estatus del Servidor</Title>
              <Text><strong>IP:</strong> {ip}</Text><br />
              <Text><strong>Puerto:</strong> {port}</Text><br />
              <Text><strong>Estado:</strong> <Tag color={status === "activo" ? "green" : "red"}>{status}</Tag></Text><br />
              {status === "activo" && pid && (
                <Text><strong>PID:</strong> {pid}</Text>
              )}
              <br />
              <Space style={{ marginTop: 12 }}>
                <Button type="primary" icon={<PoweroffOutlined />} disabled={status === "activo"} onClick={iniciarServidor} loading={spin}>Iniciar</Button>
                <Button danger disabled={status !== "activo"} onClick={detenerServidor} loading={spin}>Detener</Button>
                <Button onClick={reiniciarServidor} loading={spin}>Reiniciar</Button>
              </Space>
            </Col>
          </Row>
        )}
      </Card>
    </motion.div>
  );
};

const Dashboard = () => {
  const [selected, setSelected] = useState("inicio");

  const cerrarSesion = () => {
    document.cookie = "middleware_session=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;";
    window.location.href = window.location.origin;
  };

  const renderVista = () => {
    const contenido = (() => {
      switch (selected) {
        case "inicio": return <BackendStatus />;
        case "conexiones": return <Conexiones />;
        case "config": return <Configuracion />;
        case "log": return <LogSistema />;
        case "token": return <Token />;
        default: return <Card>No se reconoce la opción seleccionada.</Card>;
      }
    })();

    return (
      <motion.div
        key={selected}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.4 }}
      >
        <div style={{ padding: "0rem 1rem 1rem", maxWidth: 1000, margin: "0 auto" }}>
          {contenido}
        </div>
      </motion.div>
    );
  };

  return (
    <Layout style={{ minHeight: "100vh", background: "linear-gradient(145deg, #0f2027, #203a43, #2c5364)" }}>
      <Sider
        breakpoint="lg"
        collapsedWidth="0"
        width={220}
        style={{ backgroundColor: "#001529" }}
      >
        <div style={{ height: 64, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600, color: "white" }}>
          Middleware
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selected]}
          onClick={(e) => setSelected(e.key)}
          items={MENU_ITEMS}
        />
      </Sider>
      <Layout>
        <Header
          style={{
            background: "transparent",
            borderBottom: "none",
            padding: "0 24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <div style={{ fontWeight: 600, color: "black" }}>Middleware SharePoint</div>
          <Button icon={<LogoutOutlined />} danger onClick={cerrarSesion}>Cerrar sesión</Button>
        </Header>
        <Content style={{ margin: "0 16px 0" }}>
          <Row justify="center">
            <Col span={24} style={{ maxWidth: 900 }}>
              {renderVista()}
            </Col>
          </Row>
        </Content>
      </Layout>
    </Layout>
  );
};

export default Dashboard;